/* HOMAR: material drift and reading position. Existing GSAP owns all copy reveals. */
(() => {
  'use strict';
  function init() {
    window.HomarAwardMotion?.destroy();
    const reduced = matchMedia('(prefers-reduced-motion:reduce)');
    const desktop = matchMedia('(min-width:901px)');
    const fine = matchMedia('(hover:hover) and (pointer:fine)');
    const root = document.documentElement;
    const listeners = [];
    const clamp = (value, min, max) => Math.max(min, Math.min(max, value));
    const progress = document.createElement('div');
    progress.className = 'au-page-progress';
    progress.setAttribute('aria-hidden', 'true');
    document.body.append(progress);
    let frame = 0, scenes = [], pageHeight = 1, heroPointer = { x: 0, y: 0 };
    let destroyed = false;
    const canMove = () => !reduced.matches && desktop.matches && !document.hidden;
    const listen = (target, type, callback, options) => {
      target.addEventListener(type, callback, options);
      listeners.push(() => target.removeEventListener(type, callback, options));
    };
    function reset(scene) {
      for (const name of scene.variables) scene.layer.style.removeProperty(name);
    }
    function update() {
      frame = 0;
      if (destroyed || document.hidden) return;
      // Batch geometry reads before any style writes. No layout property is animated.
      const height = innerHeight;
      const maximum = Math.max(0, pageHeight - height);
      const position = maximum ? clamp(scrollY / maximum, 0, 1) : 0;
      const moving = canMove();
      const snapshots = scenes.map(scene => ({
        scene,
        rect: moving && scene.visible ? scene.host.getBoundingClientRect() : null,
      }));
      progress.style.setProperty('--au-progress', position.toFixed(5));
      for (const { scene, rect } of snapshots) {
        scene.rect = rect;
        if (!rect) { reset(scene); continue; }
        const depth = clamp((height / 2 - (rect.top + rect.height / 2)) / height, -1, 1);
        if (scene.kind === 'hero') {
          scene.layer.style.setProperty('--au-hero-x', `${heroPointer.x.toFixed(2)}px`);
          scene.layer.style.setProperty('--au-hero-y', `${(depth * 14 + heroPointer.y).toFixed(2)}px`);
        } else {
          scene.layer.style.setProperty('--au-science-y', `${(depth * 18).toFixed(2)}px`);
        }
      }
    }
    function schedule() {
      if (!destroyed && !document.hidden && !frame) frame = requestAnimationFrame(update);
    }
    function measure() {
      pageHeight = Math.max(document.body.scrollHeight, root.scrollHeight);
      schedule();
    }
    const visibility = new IntersectionObserver(entries => {
      for (const entry of entries) {
        const scene = scenes.find(item => item.host === entry.target);
        if (scene) scene.visible = entry.isIntersecting;
      }
      schedule();
    }, { threshold: 0 });
    function collect() {
      visibility.disconnect();
      scenes.forEach(reset);
      scenes = [];
      for (const [kind, selector, hostSelector, variables] of [
        ['hero', '.hero-media', '.hero', ['--au-hero-x', '--au-hero-y']],
        ['science', '.science-backdrop', '.science-screen', ['--au-science-y']],
      ]) {
        document.querySelectorAll(selector).forEach(layer => {
          const host = layer.closest(hostSelector) || layer.closest('#expertise');
          if (!host) return;
          scenes.push({ kind, layer, host, variables, visible: false, rect: null });
          visibility.observe(host);
        });
      }
      measure();
    }
    // Pointer depth is only a few pixels on decorative media. Controls and text never move.
    function point(event) {
      const hero = scenes.find(scene => scene.kind === 'hero' && scene.visible);
      if (!hero?.rect || !fine.matches || !canMove() || event.pointerType === 'touch') return;
      const rect = hero.rect;
      if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) {
        if (heroPointer.x || heroPointer.y) { heroPointer = { x: 0, y: 0 }; schedule(); }
        return;
      }
      heroPointer = {
        x: (clamp((event.clientX - rect.left) / rect.width, 0, 1) - .5) * 8,
        y: (clamp((event.clientY - rect.top) / rect.height, 0, 1) - .5) * 5,
      };
      schedule();
    }
    function settle() {
      heroPointer = { x: 0, y: 0 };
      if (document.hidden || !canMove()) {
        cancelAnimationFrame(frame); frame = 0;
        scenes.forEach(reset);
      }
      schedule();
    }
    listen(window, 'scroll', schedule, { passive: true });
    listen(window, 'resize', measure, { passive: true });
    listen(document, 'pointermove', point, { passive: true });
    listen(document, 'pointerleave', settle);
    listen(document, 'pointercancel', settle);
    listen(document, 'visibilitychange', settle);
    listen(reduced, 'change', settle);
    listen(desktop, 'change', settle);
    listen(fine, 'change', settle);
    listen(document, 'shopify:section:load', collect);
    listen(document, 'shopify:section:unload', collect);
    listen(window, 'pagehide', () => { cancelAnimationFrame(frame); frame = 0; scenes.forEach(reset); });
    listen(window, 'pageshow', measure);
    const resize = new ResizeObserver(measure);
    resize.observe(document.body);
    window.HomarAwardMotion = {
      refresh: collect,
      destroy() {
        destroyed = true;
        cancelAnimationFrame(frame);
        visibility.disconnect(); resize.disconnect();
        listeners.forEach(remove => remove());
        scenes.forEach(reset);
        progress.remove();
      },
    };
    collect();
    document.fonts?.ready.then(() => { if (!destroyed) measure(); });
    // Legacy homepage scripts construct the science wrapper at DOMContentLoaded.
    requestAnimationFrame(() => { if (!destroyed) collect(); });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
})();
